import { useState, useEffect } from "react";
import api from "../../api/axios";
import Swal from "sweetalert2";

import {
  FaCalendarAlt,
  FaRupeeSign,
  FaUser,
  FaFileAlt,
  FaHandshake,
  FaBuilding,
} from "react-icons/fa";

import "../../assets/css/Renewal/RenewalCreate.css";

export default function RenewalCreate({
  dealId = null,
  leadId = null,
  organizationId = null,
  onClose,
  onSuccess,
}) {
  const [users, setUsers] = useState([]);
  const [deals, setDeals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);

  // Standalone mode selection
  const [entityType, setEntityType] = useState(leadId ? "lead" : "deal");
  const [selectedEntityId, setSelectedEntityId] = useState(dealId || leadId || "");
  const [effectiveOrgId, setEffectiveOrgId] = useState(organizationId || null);
  const [selectedOrgName, setSelectedOrgName] = useState("");

  const [form, setForm] = useState({
    expiry_date: "",
    renewal_amount: "",
    assigned_to: "",
    notes: "",
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const usersRes = await api.get("/users");
        setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
      }

      // If standalone (no dealId or leadId provided as props), fetch deals and leads
      if (!dealId && !leadId) {
        try {
          const [dealsRes, leadsRes] = await Promise.all([
            api.get("/deal/").catch(() => ({ data: [] })),
            api.get("/lead/list").catch(() => ({ data: [] })),
          ]);
          setDeals(Array.isArray(dealsRes.data) ? dealsRes.data : []);
          setLeads(Array.isArray(leadsRes.data) ? leadsRes.data : []);
        } catch (err) {
          console.error("Failed to load deals or leads for renewal:", err);
        }
      }
    };

    fetchInitialData();
  }, [dealId, leadId]);

  const handleEntitySelect = (val) => {
    setSelectedEntityId(val);
    if (!val) {
      setEffectiveOrgId(null);
      setSelectedOrgName("");
      return;
    }

    if (entityType === "deal") {
      const deal = deals.find((d) => String(d.id) === String(val));
      if (deal) {
        setEffectiveOrgId(deal.organization_id);
        setSelectedOrgName(deal.organization_name || deal.organization?.name || "");
        if (deal.assigned_to) {
          setForm((prev) => ({ ...prev, assigned_to: String(deal.assigned_to) }));
        }
        if (deal.deal_amount || deal.amount) {
          setForm((prev) => ({
            ...prev,
            renewal_amount: String(deal.deal_amount || deal.amount),
          }));
        }
      }
    } else {
      const lead = leads.find((l) => String(l.id) === String(val));
      if (lead) {
        setEffectiveOrgId(lead.organization_id);
        setSelectedOrgName(lead.organization_name || lead.organization?.name || "");
        if (lead.assigned_to) {
          setForm((prev) => ({ ...prev, assigned_to: String(lead.assigned_to) }));
        }
        if (lead.value) {
          setForm((prev) => ({ ...prev, renewal_amount: String(lead.value) }));
        }
      }
    }

    setErrors((prev) => ({ ...prev, entity: "" }));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === "renewal_amount") {
      const rawValue = value.replace(/,/g, "").replace(/\D/g, "");

      setForm((prev) => ({
        ...prev,
        [name]: rawValue,
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    // clear error on change
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const isBlank = (val) => !val || String(val).trim() === "";

  const validate = () => {
    const newErrors = {};

    if (!dealId && !leadId) {
      if (isBlank(selectedEntityId)) {
        newErrors.entity = `Please select a ${entityType === "deal" ? "Deal" : "Lead"}`;
      } else if (!effectiveOrgId) {
        newErrors.entity = "Selected record must be associated with an organization";
      }
    }

    if (isBlank(form.expiry_date)) {
      newErrors.expiry_date = "Expiry date is required";
    }

    if (isBlank(form.renewal_amount)) {
      newErrors.renewal_amount = "Renewal amount is required";
    } else if (Number(form.renewal_amount) <= 0) {
      newErrors.renewal_amount = "Amount must be greater than 0";
    }

    if (isBlank(form.assigned_to)) {
      newErrors.assigned_to = "Please select a user";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    let finalDealId = null;
    let finalLeadId = null;

    if (dealId) {
      finalDealId = Number(dealId);
    } else if (leadId) {
      finalLeadId = Number(leadId);
    } else if (entityType === "deal" && selectedEntityId) {
      finalDealId = Number(selectedEntityId);
    } else if (entityType === "lead" && selectedEntityId) {
      finalLeadId = Number(selectedEntityId);
    }

    const finalOrgId = organizationId ? Number(organizationId) : effectiveOrgId ? Number(effectiveOrgId) : null;

    try {
      setLoading(true);

      await api.post("/renewal/create", {
        organization_id: finalOrgId,
        deal_id: finalDealId,
        lead_id: finalLeadId,
        amount: Number(form.renewal_amount),
        next_renewal_date: form.expiry_date,
        assigned_to: Number(form.assigned_to),
        notes: form.notes,
      });

      await Swal.fire({
        icon: "success",
        title: "Renewal Created",
        timer: 1200,
        showConfirmButton: false,
      });

      onSuccess?.();
      onClose?.();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: err.response?.data?.detail || "Failed to create renewal",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="renewal-overlay" onClick={onClose}>
      <div className="renewal-card" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="renewal-header">
          <div>
            <h3>
              {dealId
                ? "Renewal Information (Deal)"
                : leadId
                  ? "Renewal Information (Lead)"
                  : "Create New Renewal"}
            </h3>
            {selectedOrgName && (
              <p style={{ margin: "4px 0 0 0", color: "#64748b", fontSize: "13px" }}>
                Organization: <strong>{selectedOrgName}</strong>
              </p>
            )}
          </div>

          <button className="renewal-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {/* FORM */}
        <form onSubmit={submit}>
          <div className="renewal-grid">
            {/* Standalone entity selector when not opened inside a specific lead or deal */}
            {!dealId && !leadId && (
              <>
                <div className="renewal-group">
                  <label>
                    Link Renewal To <span>*</span>
                  </label>
                  <div className="renewal-input">
                    <FaHandshake />
                    <select
                      value={entityType}
                      onChange={(e) => {
                        const newType = e.target.value;
                        setEntityType(newType);
                        setSelectedEntityId("");
                        setEffectiveOrgId(null);
                        setSelectedOrgName("");
                      }}
                    >
                      <option value="deal">Deal</option>
                      <option value="lead">Lead</option>
                    </select>
                  </div>
                </div>

                <div className={`renewal-group ${errors.entity ? "has-error" : ""}`}>
                  <label>
                    Select {entityType === "deal" ? "Deal" : "Lead"} <span>*</span>
                  </label>
                  <div className="renewal-input">
                    <FaBuilding />
                    <select
                      value={selectedEntityId}
                      onChange={(e) => handleEntitySelect(e.target.value)}
                    >
                      <option value="">
                        -- Choose {entityType === "deal" ? "Deal" : "Lead"} --
                      </option>
                      {entityType === "deal"
                        ? deals.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.title} {d.organization_name ? `(${d.organization_name})` : ""}
                          </option>
                        ))
                        : leads.map((l) => (
                          <option key={l.id} value={l.id}>
                            {l.title} {l.organization_name ? `(${l.organization_name})` : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                  {errors.entity && <small className="error-text">{errors.entity}</small>}
                </div>
              </>
            )}

            {/* DATE */}
            <div className={`renewal-group ${errors.expiry_date ? "has-error" : ""}`}>
              <label>
                New Renewal Date <span>*</span>
              </label>

              <div className="renewal-input">
                <FaCalendarAlt />
                <input
                  type="date"
                  name="expiry_date"
                  value={form.expiry_date}
                  onChange={handleChange}
                />
              </div>

              {errors.expiry_date && (
                <small className="error-text">{errors.expiry_date}</small>
              )}
            </div>

            {/* AMOUNT */}
            <div className={`renewal-group ${errors.renewal_amount ? "has-error" : ""}`}>
              <label>
                Expected Renewal Amount <span>*</span>
              </label>

              <div className="renewal-input">
                <FaRupeeSign />
                <input
                  type="text"
                  name="renewal_amount"
                  value={
                    form.renewal_amount
                      ? Number(form.renewal_amount).toLocaleString("en-IN")
                      : ""
                  }
                  onChange={handleChange}
                  placeholder="1,25,000"
                  required
                />
              </div>

              {errors.renewal_amount && (
                <small className="error-text">{errors.renewal_amount}</small>
              )}
            </div>

            {/* USER */}
            <div
              className={`renewal-group renewal-full ${errors.assigned_to ? "has-error" : ""
                }`}
            >
              <label>
                Assigned User <span>*</span>
              </label>

              <div className="renewal-input">
                <FaUser />
                <select
                  name="assigned_to"
                  value={form.assigned_to}
                  onChange={handleChange}
                >
                  <option value="">Select User</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              {errors.assigned_to && (
                <small className="error-text">{errors.assigned_to}</small>
              )}
            </div>

            {/* NOTES */}
            <div className="renewal-group renewal-full">
              <label>Notes</label>

              <div className="renewal-textarea">
                <FaFileAlt />
                <textarea
                  name="notes"
                  value={form.notes}
                  onChange={handleChange}
                  placeholder="Optional notes..."
                />
              </div>
            </div>
          </div>

          {/* ACTIONS */}
          <div className="renewal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? "Saving..." : "Create Renewal"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}